import React, { useState, useEffect } from 'react';
import { FaQuestionCircle, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';

const Help = () => {
  const [openSection, setOpenSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.title = "Help Center - SastaMastaRide";
  }, []);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How do I create an account?",
          a: "Click on the 'Register' button in the top right corner, fill in your details including name, email, phone number, and password. Verify your email and phone number to complete registration."
        },
        {
          q: "How do I find a ride?",
          a: "Go to the home page, enter your origin and destination locations, select your preferred date and vehicle type, then click 'Search Rides'. Browse available rides and click on one to see details and book."
        },
        {
          q: "How do I offer a ride?",
          a: "First, verify yourself as a rider by going to 'Profile' > 'Rider Verification' and submitting required documents. Once verified, go to 'Create Trip' to post your ride with origin, destination, date, time, and pricing details."
        }
      ]
    },
    {
      category: "Booking & Payments",
      questions: [
        {
          q: "How do I book a ride?",
          a: "Search for available rides, select a ride that matches your needs, review the trip details, and click 'Book Ride'. You can pay online using UPI, credit/debit card, or choose cash payment option."
        },
        {
          q: "What payment methods are accepted?",
          a: "We accept online payments via UPI, credit cards, debit cards, and net banking. You can also choose cash payment, which will be confirmed by the rider after the trip."
        },
        {
          q: "Can I cancel a booking?",
          a: "Yes, you can cancel a booking. Cancellation policies vary based on timing - check the trip details for specific cancellation terms. Refunds for online payments are processed within 5-7 business days."
        },
        {
          q: "How do I get a refund?",
          a: "Refunds are automatically processed to your original payment method within 5-7 business days after cancellation approval. For cash payments, no refund is needed as payment is made directly to the rider."
        }
      ]
    },
    {
      category: "Rider Information",
      questions: [
        {
          q: "How do I become a verified rider?",
          a: "Go to your Profile, click on 'Rider Verification', and submit required documents including driving license, vehicle registration, and identity proof. Our team will verify your documents within 24-48 hours."
        },
        {
          q: "How do I create a trip?",
          a: "After verification, go to 'Create Trip', enter your origin and destination, select date and time, set your price per seat, add any waypoints, and publish your trip. Passengers can then book seats on your trip."
        },
        {
          q: "How do I get paid?",
          a: "After completing a trip, payments are credited to your wallet. You can request a payout from your wallet, which will be processed within 2-3 business days to your registered bank account."
        },
        {
          q: "What is the platform fee?",
          a: "SastaMastaRide charges a small platform fee (typically 10-15%) on each completed trip to cover operational costs, payment processing, and platform maintenance."
        }
      ]
    },
    {
      category: "Safety & Security",
      questions: [
        {
          q: "How do you ensure rider safety?",
          a: "All riders are verified with valid documents. We have a rating and review system, real-time trip tracking, and 24/7 support. Passengers can report any issues, and we take immediate action."
        },
        {
          q: "What should I do in case of an emergency?",
          a: "Use the emergency contact feature in the app, contact our 24/7 support helpline, or use the trip tracking feature to share your location with trusted contacts."
        },
        {
          q: "How are disputes resolved?",
          a: "Contact our support team with trip details and evidence. We investigate all disputes fairly and take appropriate action including refunds, account suspension, or other measures as needed."
        }
      ]
    },
    {
      category: "Account & Profile",
      questions: [
        {
          q: "How do I update my profile?",
          a: "Go to 'Profile' in the navigation menu, click 'Edit Profile', update your information, and save changes. You can update your name, phone, profile picture, and preferences."
        },
        {
          q: "How do I change my password?",
          a: "Go to Profile > Settings, click on 'Change Password', enter your current password and new password, then confirm the change."
        },
        {
          q: "How do I delete my account?",
          a: "Contact our support team to request account deletion. Note that you must complete all active trips and resolve any pending payments before account deletion."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          q: "The app is not loading properly",
          a: "Try clearing your browser cache, ensure you have a stable internet connection, or try using a different browser. If issues persist, contact our technical support team."
        },
        {
          q: "I'm not receiving notifications",
          a: "Check your browser notification settings and ensure notifications are enabled for our website. Also check your account notification preferences in Profile > Settings."
        },
        {
          q: "How do I contact support?",
          a: "You can reach us via email at support@sastamastaride.com, through the in-app chat feature, or call our 24/7 helpline. We typically respond within 2-4 hours."
        }
      ]
    }
  ];

  const filteredFAQs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(qa => 
      qa.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      qa.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Help Center</h1>
        <p className="text-gray-600 mb-6">Find answers to common questions and get help with using SastaMastaRide</p>

        {/* Search Bar */}
        <div className="relative mb-8">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* FAQ Sections */}
        <div className="space-y-4">
          {filteredFAQs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(categoryIndex)}
                className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
              >
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <FaQuestionCircle className="mr-3 text-blue-600" />
                  {category.category}
                </h2>
                {openSection === categoryIndex ? (
                  <FaChevronUp className="text-gray-600" />
                ) : (
                  <FaChevronDown className="text-gray-600" />
                )}
              </button>
              
              {openSection === categoryIndex && (
                <div className="px-6 py-4 space-y-4">
                  {category.questions.map((faq, faqIndex) => (
                    <div key={faqIndex} className="border-b border-gray-100 pb-4 last:border-b-0">
                      <h3 className="font-semibold text-gray-800 mb-2">{faq.q}</h3>
                      <p className="text-gray-600">{faq.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div className="mt-8 bg-blue-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Still need help?</h2>
          <p className="text-gray-600 mb-4">
            Can't find what you're looking for? Our support team is here to help you 24/7.
          </p>
          <div className="space-y-2 text-gray-700">
            <p><strong>Email:</strong> support@sastamastaride.com</p>
            <p><strong>Phone:</strong> +91-1800-XXX-XXXX</p>
            <p><strong>Response Time:</strong> Within 2-4 hours</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;

